import type { Namespace, TFunction } from 'i18next';
import { permissionDef, type PermissionGroupId, type PermissionKey } from '@nexa/auth';

// Localized permission / group / system-role names (users-roles-permissions R5; reused by
// the R6 screens).
//
// The catalog's `labelKey` is `permissions.<key>` — but several keys are both a label and
// the prefix of another (`disruptions.contain` / `disruptions.contain.force`,
// `care.issue` / `care.issue.custom`, …), which a dot-nested JSON bundle cannot hold. So
// the copy lives FLAT in `admin.json › permissionLabels`, keyed by the permission key with
// its dots spelled `/` (`disruptions/contain/force`) — `/` can never occur in a key
// (PERMISSION_KEY_PATTERN), so the mapping is lossless, and no key holds a dot, which the
// locale-parity test's dotted leaf walk requires.

/** The i18n key of a permission's label, e.g. `admin:permissionLabels.cases/view`. */
export function permissionLabelKey(key: PermissionKey): string {
  return `admin:permissionLabels.${key.split('.').join('/')}`;
}

/** A permission's localized label; the catalog's Spanish label if a locale lacks it. */
export function permissionLabel<N extends Namespace>(t: TFunction<N>, key: PermissionKey): string {
  return t(permissionLabelKey(key), { defaultValue: permissionDef(key)?.label ?? key });
}

/** A permission group's localized heading (§4.x). */
export function permissionGroupLabel<N extends Namespace>(
  t: TFunction<N>,
  group: PermissionGroupId,
): string {
  return t(`admin:permissionGroups.${group}`);
}

/** The group a permission belongs to, or `undefined` for a non-catalog key. */
export function permissionGroupOf(key: PermissionKey): PermissionGroupId | undefined {
  return permissionDef(key)?.group;
}

/**
 * A role's display name: system roles are translated (`admin:roleNames.<key>`), custom
 * roles keep the name the airline gave them.
 */
export function roleLabel<N extends Namespace>(
  t: TFunction<N>,
  role: { key: string; name: string; system: boolean },
): string {
  return role.system ? t(`admin:roleNames.${role.key}`, { defaultValue: role.name }) : role.name;
}
